import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import SwiperCore, { Autoplay, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import "./PostDetails.css";
import ImageFrame from "../ImageFrame";
import placeholder from "./assets/image.png";
import pfp from "../assets/pfp.jpg";
import dateIcon from "./assets/svg/date.svg";
import chevronLeft from "./assets/svg/chevron-left-large.svg";
import chevronRight from "./assets/svg/chevron-right-large.svg";
import arrowIcon from "./assets/svg/arrow.svg";
import Footer from "../Footer";

SwiperCore.use([Autoplay, Pagination]);

const decodeEntities = (str) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

const getExcerpt = (post, length = 140) => {
  const raw = post.excerpt?.rendered ?? "";
  const stripped = raw.replace(/<[^>]+>/g, "");
  return decodeEntities(stripped).substring(0, length) + "…";
};

const hasCropTag = (post) => {
  const tags =
    post?._embedded?.["wp:term"]
      ?.flat()
      ?.filter((t) => t.taxonomy === "post_tag") ?? [];

  return tags.some((t) => t.slug === "crop" || t.name === "crop");
};

const getAuthorDisplay = (author) => {
  const isAdmin =
    author?.slug === "admin" || author?.name?.toLowerCase() === "admin";

  return {
    name: isAdmin ? "BeszÉLJ Egyesület" : author?.name || "-",
    avatar: isAdmin
      ? pfp
      : author?.avatar_urls?.["96"] ||
        author?.avatar_urls?.["48"] ||
        placeholder,
  };
};

const isEmojiImage = (el) => {
  if (!el || el.tagName !== "IMG") return false;

  const src = el.getAttribute("src") || "";
  const className = el.getAttribute("class") || "";
  const alt = el.getAttribute("alt") || "";
  const width = el.getAttribute("width") || "";
  const height = el.getAttribute("height") || "";

  const emojiAltRegex =
    /^[\p{Emoji_Presentation}\p{Emoji}\u200d\ufe0f\s]+$/u;

  return (
    el.classList.contains("emoji") ||
    el.classList.contains("wp-smiley") ||
    className.includes("emoji") ||
    className.includes("wp-smiley") ||
    src.includes("/emoji/") ||
    src.includes("emoji") ||
    src.includes("twemoji") ||
    src.includes("s.w.org/images/core/emoji") ||
    src.includes("emoji.php") ||
    src.includes("wp-includes/images/smilies") ||
    (alt.length > 0 && alt.length <= 12 && emojiAltRegex.test(alt)) ||
    ((width === "1" || width === "16" || width === "18" || width === "20") &&
      (height === "1" || height === "16" || height === "18" || height === "20") &&
      alt.length > 0 &&
      alt.length <= 12)
  );
};

const translatedPosts = {
  "osztrak-magyar-dadogoegyesuletek-talalkozoja-becsben": {
    title: "Osztrák–magyar kapcsolatépítő hétvége Bécsben",
    author: {
      name: "Plöchl Patrik",
      role: "Nemzetközi kapcsolatok – ÖSIS",
      roleUrl: "https://oesis.at/",
      avatar:
        "https://oesis.at/wp-content/uploads/2026/01/IMG_0108-e1768842244766.jpg",
    },
    paragraphs: [
      "Magyar vendégeink már péntek este megérkeztek, és egy kellemes üdvözlő találkozóval indítottuk a hétvégét egy helyi vendéglátóhelyen. Az este gyorsan tovább alakult, és végül a szomszédos karaoke bárban ért véget, ahol ugyan nem mindig sikerült minden hangot eltalálni, a hangulat viszont annál jobb volt.",

      "A szombat nyugodtan kezdődött egy késői brunch-csal a Karmelitermarkton. Ezután a csoport kettévált: néhányan parlamenti vezetésen vettek részt, mások a Pillangóházat látogatták meg. Ezt követően kisebb csoportokban fedeztük fel Bécset.",

      "Este következett a hétvége fénypontja: az első Stamily Café Ausztriában. Ez a Stamily nemzetközi találkozási formátuma, egy nyitott, angol nyelvű találkozó dadogó emberek számára, amelyet mindenhol megszerveznek, ahol a világ különböző pontjain Stamily-tagok találkoznak. A cél egy nyitott tér megteremtése a tapasztalatcserére és új kapcsolatok kialakítására az érintettek között.",

      "A koncepció teljes mértékben bevált. Sok beszélgetés, új kapcsolat és végig pozitív hangulat alakult ki egészen a hely zárásáig. A személyes eszmecsere mellett a jövőbeli lehetőségek is szóba kerültek: már az ÖSIS budapesti viszontlátogatásának első tervei is körvonalazódtak.",

      "Különösen örömteli volt, hogy nemcsak osztrák és magyar résztvevők találtak egymásra, hanem nemzetközi érdeklődők, valamint olyan új emberek is, akik korábban nem kapcsolódtak az önsegítő közösséghez. Az újonnan kialakult kapcsolatok közül sok már ezen a hétvégén megerősödött.",

      "A vasárnap még lehetőséget adott néhány mélyebb beszélgetésre, mielőtt mindenki hazaindult.",

      "Összességében a hétvége teljes siker volt, és nagyon világosan megmutatta, milyen nagy lehetőség rejlik a nemzetközi kapcsolatépítésben. Nagy köszönet illeti az ÖSIS Wien szervezőcsapatát — Stefit, Martint és Katarinát —, a magyar egyesületet, különösen Gergőt és Bercelt, valamint a Stamilyt a Stamily Café nagyszerű koncepciójáért.",
    ],
  },
};

export default function PostDetails() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [authorData, setAuthorData] = useState(null);
  const [sidebarPosts, setSidebarPosts] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const relatedSwiperRef = useRef(null);
  const sidebarSwiperRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setAuthorData(null);
  }, [slug]);

  useEffect(() => {
    fetch(
      `https://beszeljegyesulet.hu/wp/wp-json/wp/v2/posts?slug=${slug}&_embed`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data?.length) {
          const p = data[0];
          setPost(p);

          const authorId = p.author;
          if (authorId) {
            fetch(
              `https://beszeljegyesulet.hu/wp/wp-json/wp/v2/users/${authorId}`
            )
              .then((r) => r.json())
              .then((u) => setAuthorData(u))
              .catch(() => {});
          }
        }
      })
      .catch((err) => console.error("Hiba:", err));
  }, [slug]);

  useEffect(() => {
    fetch(
      `https://beszeljegyesulet.hu/wp/wp-json/wp/v2/posts?_embed&per_page=12`
    )
      .then((res) => res.json())
      .then((data) => {
        const cleaned = Array.isArray(data)
          ? data.filter((p) => p.slug !== slug)
          : [];

        setSidebarPosts(cleaned.slice(0, 6));
        setRelatedPosts(cleaned.slice(6, 12));
      })
      .catch((err) => console.error("Hiba a listában:", err));
  }, [slug]);

  const handleRelatedPrev = () => {
    const swiper = relatedSwiperRef.current;
    if (!swiper) return;

    if (swiper.isBeginning) swiper.slideTo(swiper.slides.length - 1);
    else swiper.slidePrev();
  };

  const handleRelatedNext = () => {
    const swiper = relatedSwiperRef.current;
    if (!swiper) return;

    if (swiper.isEnd) swiper.slideTo(0);
    else swiper.slideNext();
  };

  useEffect(() => {
    if (!post) return;

    const timer = setTimeout(() => {
      const wpContent = document.querySelector(".wp-content");
      if (!wpContent) return;

      const allEls = wpContent.querySelectorAll("*");

      allEls.forEach((el) => {
        el.style.boxSizing = "border-box";

        const isEmoji = isEmojiImage(el);

        if (isEmoji) {
          el.style.display = "inline";
          el.style.width = "1em";
          el.style.height = "1em";
          el.style.minWidth = "1em";
          el.style.minHeight = "1em";
          el.style.maxWidth = "1em";
          el.style.maxHeight = "1em";
          el.style.margin = "0 0.05em";
          el.style.verticalAlign = "-0.1em";
          el.style.borderRadius = "0";
          el.style.objectFit = "contain";
          return;
        }

        el.style.maxWidth = "100%";

        if (el.tagName === "IMG") {
          el.style.width = "100%";
          el.style.height = "auto";
        }

        if (el.tagName === "VIDEO" || el.tagName === "IFRAME") {
          el.style.width = "100%";
          el.style.height = "auto";
        }

        if (el.style.width && parseInt(el.style.width, 10) > window.innerWidth) {
          el.style.width = "100%";
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [post]);

  if (!post) return <div className="loading">Betöltés...</div>;

  const featured =
    post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || placeholder;

  const crop = hasCropTag(post);

  const embeddedAuthor = post._embedded?.author?.[0];
  const author = authorData || embeddedAuthor;
  const { name: authorName, avatar: authorAvatar } = getAuthorDisplay(author);

  const formattedDate = new Date(post.date).toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const translatedPost = translatedPosts[slug];

  return (
    <main className="postdetails">
      <div className="post-hero-wrap">
        {crop ? (
          <div className="post-hero-frame post-hero-frame--crop">
            <img
              src={featured}
              alt={post.title.rendered}
              className="post-hero-image post-hero-image--crop"
            />
          </div>
        ) : (
          <div className="post-hero-frame">
            <div
              className="post-hero-blur"
              style={{ backgroundImage: `url(${featured})` }}
              aria-hidden="true"
            />
            <img
              src={featured}
              alt={post.title.rendered}
              className="post-hero-image"
            />
          </div>
        )}
      </div>

      <section className="post-content-wrapper">
        <article className="post-main">
          <h1 dangerouslySetInnerHTML={{ __html: post.title.rendered }} />

          <div
            className="wp-content"
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
          />
        </article>

        <aside className="post-sidebar">
          <div className="sidebar-published">{formattedDate.toUpperCase()}</div>

          <div className="sidebar-author">
            <img src={authorAvatar} alt={authorName} />

            <div className="sidebar-author-info">
              <span className="name">{authorName.toUpperCase()}</span>
              <span className="role">Szerző</span>
            </div>
          </div>

          <div className="sidebar-swiper">
            <Swiper
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop={true}
              pagination={{ clickable: true }}
              spaceBetween={16}
              slidesPerView={1}
              onSwiper={(swiper) => {
                sidebarSwiperRef.current = swiper;
              }}
            >
              {sidebarPosts.map((item) => {
                const img =
                  item._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
                  placeholder;

                return (
                  <SwiperSlide key={item.id}>
                    <div className="sidebar-card">
                      <div className="slide-image-frame">
                        <div
                          className="slide-image-blur"
                          style={{ backgroundImage: `url(${img})` }}
                          aria-hidden="true"
                        />
                        <img
                          src={img}
                          alt={item.title.rendered}
                          className="slide-img"
                        />
                      </div>

                      <div className="slide-content">
                        <div className="meta-item">
                          <div className="icon">
                            <img src={dateIcon} alt="Dátum" />
                          </div>
                          <span>
                            <b>
                              {new Date(item.date).toLocaleDateString("hu-HU", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </b>
                          </span>
                        </div>

                        <Link
                          to={`/posts/${item.slug}`}
                          style={{ textDecoration: "none" }}
                        >
                          <h4
                            className="heading-sm"
                            dangerouslySetInnerHTML={{
                              __html: item.title.rendered,
                            }}
                          />
                        </Link>

                        <p>{getExcerpt(item, 140)}</p>

                        <Link
                          to={`/posts/${item.slug}`}
                          className="event-link"
                        >
                          Tudj meg többet{" "}
                          <img
                            src={arrowIcon}
                            alt="nyíl ikon"
                            className="arrow-icon"
                          />
                        </Link>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </aside>
      </section>

      {translatedPost && (
        <section className="translated-post-wrapper">
          <article className="translated-post">
            <h2>{translatedPost.title}</h2>

            <div className="translated-post-content">
              {translatedPost.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </article>

          <aside className="translated-post-sidebar">
            <div className="sidebar-author translated-sidebar-author">
              <img
                src={translatedPost.author.avatar}
                alt={translatedPost.author.name}
              />

              <div className="sidebar-author-info">
                <span className="name">
                  {translatedPost.author.name.toUpperCase()}
                </span>

                {translatedPost.author.roleUrl ? (
                  <a
                    className="role"
                    href={translatedPost.author.roleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {translatedPost.author.role}
                  </a>
                ) : (
                  <span className="role">{translatedPost.author.role}</span>
                )}
              </div>
            </div>
          </aside>
        </section>
      )}

      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <h2 className="related-heading">További cikkek</h2>

          <div className="related-carousel-wrap">
            <button
              className="custom-nav related-prev"
              aria-label="Előző"
              onClick={handleRelatedPrev}
            >
              <img src={chevronLeft} alt="" />
            </button>

            <button
              className="custom-nav related-next"
              aria-label="Következő"
              onClick={handleRelatedNext}
            >
              <img src={chevronRight} alt="" />
            </button>

            <Swiper
              className="related-carousel"
              spaceBetween={24}
              slidesPerView={3}
              slidesPerGroup={1}
              loop={true}
              onSwiper={(swiper) => {
                relatedSwiperRef.current = swiper;
              }}
              pagination={{ el: ".related-pagination", clickable: true }}
            >
              {relatedPosts.map((relPost) => {
                const relFeatured =
                  relPost._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
                  placeholder;

                return (
                  <SwiperSlide key={relPost.id}>
                    <div className="info-card">
                      <ImageFrame
                        post={relPost}
                        src={relFeatured}
                        alt={relPost.title.rendered}
                      />

                      <div className="info-content">
                        <h3
                          className="heading-md"
                          dangerouslySetInnerHTML={{
                            __html: relPost.title.rendered,
                          }}
                        />

                        <p>{getExcerpt(relPost, 180)}</p>

                        <Link
                          to={`/posts/${relPost.slug}`}
                          className="info-link"
                        >
                          Tovább{" "}
                          <img
                            src={arrowIcon}
                            alt="nyíl ikon"
                            className="arrow-icon"
                          />
                        </Link>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}

              <div className="swiper-pagination related-pagination"></div>
            </Swiper>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}